# Route: /admin/roles
# Role management: list roles with their name and description

page-admin-role-list:
  type: Page
  archetype: list
  meta:
    title: $t.admin.roles.meta_title
    auth: required
  data:
    roles:
      model: RoleDto[]
      refresh: on_mount
  spatial:
    display: flex
    direction: column
    gap: $spacing.lg
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
    responsive:
      sm:
        padding: $spacing.sm
        padding_inline: $spacing.sm
        gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - role-list-headerbar
    - role-list-table

# ── Header bar (title + actions on one row) ──────────────────────────────────
# Generic "header bar" composition: the page title and its action row share ONE
# flex row (justify space-between) so the action button sits on the title's
# line, not below it. This is the reusable pattern for any page whose actions
# are richer than the PageHeader `actions:` shorthand (permission gate here) —
# no emitter change, the buttons keep their full behaviour.

role-list-headerbar:
  type: Section
  spatial:
    display: flex
    direction: row
    justify: space-between
    align: center
    gap: $spacing.md
    wrap: wrap
  children:
    - role-list-header
    - role-list-actions

role-list-header:
  type: PageHeader
  title: $t.admin.roles.title

# ── Create affordance ────────────────────────────────────────────────────────
# Wrapped in a Section carrying the permission gate (only NavItem / Section /
# SectionHeader nodes are permission-wrapped by the emitter — a bare Button is
# not). Gated on Role:create — the same permission POST /api/users/roles
# enforces — so the affordance only shows to administrators who can use it.

role-list-actions:
  type: Section
  permission: "Role:create"
  spatial:
    display: flex
    justify_content: flex-end
  children:
    - role-list-create-btn

role-list-create-btn:
  type: Button
  variant: filled
  color: info
  content: $t.admin.roles.btn_create
  a11y:
    label: $t.admin.roles.btn_create_label
  action:
    type: navigate
    route: /admin/roles/create
    # F79 — Cancel / on_success on the create screen returns to this list.
    return_context: origin_only
  spatial:
    min_height: $size.touch-target
    padding_inline: $spacing.lg
  tokens:
    radius: $radius.md

# ── Role table ───────────────────────────────────────────────────────────────

role-list-table:
  type: Table
  bind:
    rows: roles
  # M1 list-filter adoption (DEC-090) — opt-in client-side quick-filter:
  # case-insensitive substring over name + description across the loaded rows.
  filter:
    fields: [name, description]
    placeholder: $t.common.quick_filter
  a11y:
    label: $t.admin.roles.table_label
  columns:
    - key: name
      label: $t.admin.roles.col_name
      width: 2fr
    - key: description
      label: $t.admin.roles.col_description
      width: 3fr
  row_action:
    type: navigate
    route: /admin/roles/:name
    params:
      name: $row.name
  empty: role-list-empty

role-list-empty:
  type: EmptyState
  content: $t.admin.roles.empty
  body: $t.admin.roles.empty_body
